import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { Alert, Card, PageContainer, Spinner } from '@shared';
import { useAuth } from '@/features/auth/useAuth';
import { useTranslations } from '@/i18n/client';
import { createPortalApi } from '@/services/api';
import type { UserApi } from '@/services/api/user-api';

/** Where a confirmed user continues the wizard (step 2 — company info). */
const CONTINUE_TO = '/register/organization';

type VerifyState = 'verifying' | 'error' | 'success';

export interface VerifyEmailPageProps {
  /** Test-only injection point for a mock UserApi. */
  user?: UserApi;
}

/**
 * Public activation landing page for the MDenture self-registration flow.
 *
 * Reads `id`/`token` query parameters from the activation mail link, confirms
 * the e-mail via `SuiteAPI/User/ConfirmEmailV1`, establishes a JWT session and
 * continues to the (now protected) company info step. Any failure surfaces a
 * single user-safe message with no raw error details.
 *
 * Uses the same start-guard pattern as SpectraX's VerifyEmailPage to ensure
 * the single-use token is consumed at most once even under React 18 StrictMode.
 */
export function VerifyEmailPage({ user }: VerifyEmailPageProps) {
  const t = useTranslations('registration');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signInWithJwt } = useAuth();

  const id = params.get('id');
  const token = params.get('token');

  const [state, setState] = useState<VerifyState>(() => (!id || !token ? 'error' : 'verifying'));

  const startedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!id || !token || startedRef.current) {
      return;
    }
    startedRef.current = true;

    const api = user ?? createPortalApi().user;

    void (async () => {
      try {
        const result = await api.confirmEmail({ userID: id, token });
        if (!mountedRef.current) {
          return;
        }
        signInWithJwt(result.jwt, {
          id: result.userID,
          eMail: result.email,
          orgID: result.orgID,
        });
        setState('success');
        navigate(CONTINUE_TO, { replace: true });
      } catch {
        if (!mountedRef.current) {
          return;
        }
        setState('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageContainer className="md-auth">
      <Card title={t('verifyEmail.title')}>
        {state === 'verifying' ? (
          <div className="md-center">
            <Spinner label={t('verifyEmail.verifying')} />
          </div>
        ) : state === 'error' ? (
          <>
            <Alert variant="error">{t('verifyEmail.error')}</Alert>
            <p>
              <Link to="/login">{t('verifyEmail.signInLink')}</Link>
            </p>
          </>
        ) : (
          <Alert variant="success">{t('verifyEmail.success')}</Alert>
        )}
      </Card>
    </PageContainer>
  );
}
